import styled from 'styled-components'
import { motion } from 'framer-motion'
import { colors, getFontStyle } from '@diversioteam/diversio-ds'

import type {
    NotificationItemContainerProps,
    NotificationTypeIconProps,
    ActionButtonProps,
} from './NotificationItem.types'

export const NotificationItemContainer = styled(motion.div)<NotificationItemContainerProps>`
    position: relative;
    padding: 1rem 1.25rem;
    cursor: ${(props) => (props.$isDismissing ? 'default' : 'pointer')};
    transition: all 0.2s ease;
    background-color: ${colors.primaryWhite};

    border-bottom: 1px solid ${colors.primaryGray1};

    ${(props) =>
        props.$isDismissing &&
        `
    pointer-events: none;
    opacity: 0.7;
  `}

    &:hover {
        background: ${(props) => (props.$isDismissing ? 'transparent' : colors.primary10)};
    }

    &:active {
        background: ${(props) => (props.$isDismissing ? 'transparent' : colors.primaryGray2)};
    }

    &:focus {
        outline: none;
    }
`

export const NotificationContent = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    margin-bottom: 8px;
    padding: 4px 0;
`

export const NotificationTypeIcon = styled.div<NotificationTypeIconProps>`
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 0.125rem;

    ${(props) => {
        switch (props.$type) {
            case 'success':
                return `color: ${colors.green60};`
            case 'warning':
                return `color: ${colors.orange60};`
            case 'failure':
            case 'error':
                return `color: ${colors.lobster60};`
            case 'info':
                return `color: ${colors.blue60};`
            default:
                return `color: ${colors.primary60};`
        }
    }}
`

export const NotificationTextContent = styled.div`
    flex: 1;
    min-width: 0;
`

export const NotificationTitleRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    margin-bottom: 4px;
`

export const NotificationTitle = styled.h4`
    ${getFontStyle('p02Semibold')}
    color: ${colors.primary90};
    margin: 0;
    line-height: 1.3;

    /* Truncate long titles */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
`

export const NotificationDescription = styled.p`
    ${getFontStyle('p03Regular')}
    color: ${colors.primary70};
    margin: 0 0 8px 0;
    line-height: 1.4;

    /* Truncate long descriptions */
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
`

export const NotificationMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
`

export const NotificationTimestamp = styled.span`
    ${getFontStyle('p03Regular')}
    color: ${colors.primary65};
    flex-shrink: 0;
    white-space: nowrap;
`

export const NotificationUserCount = styled.span`
    ${getFontStyle('p03Regular')}
    color: ${colors.primary65};
    flex-shrink: 0;

    &::before {
        content: '•';
        margin-right: 0.5rem;
    }
`

export const UnreadIndicator = styled.div`
    width: 8px;
    height: 8px;
    background: ${colors.primary60};
    border-radius: 50%;
    flex-shrink: 0;
    margin-top: 0.4rem;
`

export const NotificationActions = styled.div`
    display: flex;
    gap: 0.5rem;
    align-items: center;
    justify-content: flex-start;
    margin-top: 0.25rem;
`

export const ActionButton = styled(motion.button)<ActionButtonProps>`
    ${getFontStyle('p03Medium')}
    padding: 0.375rem 0.75rem;
    border-radius: 0.375rem;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid transparent;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    min-height: 32px;

    ${(props) => {
        switch (props.$variant) {
            case 'primary':
                return `
          background: ${colors.primary60};
          color: ${colors.primaryWhite};
          border-color: ${colors.primary60};
          
          &:hover {
            background: ${colors.primary70};
            border-color: ${colors.primary70};
          }
          
          &:active {
            background: ${colors.primary80};
            transform: scale(0.95);
          }
        `
            case 'secondary':
                return `
          background: ${colors.primaryGray1};
          color: ${colors.primary60};
          border-color: ${colors.primaryGray2};
          
          &:hover {
            background: ${colors.primaryGray2};
            border-color: ${colors.primary60};
          }
          
          &:active {
            background: ${colors.primaryGray2};
            transform: scale(0.95);
          }
        `
            case 'dismiss':
                return `
          background: transparent;
          color: ${colors.primary60};
          border-color: transparent;
          
          &:hover {
            background: ${colors.primaryGray1};
            color: ${colors.primary90};
          }
          
          &:active {
            background: ${colors.primaryGray2};
            transform: scale(0.95);
          }
        `
            default:
                return `
          background: ${colors.primaryGray1};
          color: ${colors.primary60};
          border-color: ${colors.primaryGray2};
          
          &:hover {
            background: ${colors.primaryGray2};
            border-color: ${colors.primary60};
          }
          
          &:active {
            transform: scale(0.95);
          }
        `
        }
    }}

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;

        &:hover {
            transform: none;
        }
    }

    &:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(56, 44, 205, 0.3);
    }
`

export const ClickableArea = styled.div`
    cursor: pointer;
    flex: 1;
`

// Animation variants for Framer Motion
/* Removed unused variants to reduce noise */
